import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpenCheck,
  CalendarClock,
  Check,
  ChevronDown,
  ClipboardCopy,
  Volume2,
} from "lucide-react";
import BackButton from "components/ui/back-button";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import {
  entryLookup,
  getInboundCall,
  listKnowledgeEntries,
} from "./inboundCallsApi";
import {
  buildHandoffNote,
  citedKnowledgeEntryIds,
  formatCallTime,
  REASON_LABELS,
} from "./selectors";
import { InboundCall, KnowledgeEntry } from "./types";
import OutcomeBadge from "./OutcomeBadge";
import { knowledgeEntryPath } from "./KnowledgeBankPage";
import { APPOINTMENT_CATEGORIES_PATH, KNOWLEDGE_BANK_PATH } from "./paths";

function formatOffset(offsetSeconds: number): string {
  const minutes = Math.floor(offsetSeconds / 60);
  const seconds = String(offsetSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function formatDurationMinutes(appointment: {
  startIso: string;
  endIso: string;
}): number {
  return Math.round(
    (new Date(appointment.endIso).getTime() -
      new Date(appointment.startIso).getTime()) /
      60000
  );
}

function InboundCallDetail() {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();

  // undefined = loading, null = the load failed (unknown id, another team's call, network).
  const [call, setCall] = useState<InboundCall | null | undefined>(undefined);
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  useEffect(() => {
    if (!callId) {
      setCall(null);
      return;
    }
    let cancelled = false;
    setCall(undefined);
    getInboundCall(callId)
      .then((loadedCall) => {
        if (!cancelled) {
          setCall(loadedCall);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCall(null);
        }
      });
    // The bank only labels citations; a call must still render without it.
    listKnowledgeEntries()
      .then((loadedEntries) => {
        if (!cancelled) {
          setEntries(loadedEntries);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [callId]);
  const lookup = useMemo(() => entryLookup(entries), [entries]);

  const [copied, setCopied] = useState(false);
  const [expandedCitations, setExpandedCitations] = useState<
    Record<number, boolean>
  >({});

  const toggleCitation = (turnIndex: number) =>
    setExpandedCitations((current) => ({
      ...current,
      [turnIndex]: !current[turnIndex],
    }));

  const goBack = () => navigate("/dashboard/inbound_calls");

  if (call === undefined) {
    return (
      <div className="p-6">
        <BackButton onClick={goBack} />
        <p className="mt-4 text-sm text-gray-600">Loading call…</p>
      </div>
    );
  }
  if (call === null) {
    return (
      <div className="p-6">
        <BackButton onClick={goBack} />
        <p className="mt-4 text-sm text-gray-600" data-testid="call-load-failed">
          Could not load this call.
        </p>
      </div>
    );
  }

  const practiceName = call.practiceName ?? call.practiceId;
  const timezone =
    call.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Entries deleted since the call still leave a citation on the transcript line.
  const citedEntries = citedKnowledgeEntryIds(call).flatMap((id) => {
    const entry = lookup(id);
    return entry ? [entry] : [];
  });
  const sourceLabel = (entry: KnowledgeEntry) =>
    entry.tier === "PRACTICE"
      ? `Used information from the ${practiceName} practice knowledge bank`
      : "Used information from the organization-wide knowledge bank";

  const copyHandoffNote = () => {
    const note = buildHandoffNote(call, practiceName, timezone);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(note).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-full bg-background">
      <div className="border-b-2 border-gray-100 px-4 py-2 sticky top-0 bg-white z-10 flex items-center justify-between gap-2">
        <BackButton onClick={goBack} />
      </div>

      <div className="p-4 space-y-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              {call.callerName}
            </h2>
            <p className="text-xs text-gray-500">
              {formatCallTime(call.receivedAt, timezone)} ·{" "}
              {practiceName} · {REASON_LABELS[call.reason]}
            </p>
          </div>
          <OutcomeBadge outcome={call.outcome} status={call.status} />
        </div>

        {/* Recap and next step — most scannable position */}
        <Card className="gap-2 py-4 border-l-4 border-l-brand">
          <CardContent className="px-4 space-y-3">
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">
                Recap
              </h3>
              <p className="text-sm text-gray-800" data-testid="call-recap">
                {call.recapText}
              </p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">
                Next step
              </h3>
              <p className="text-sm text-gray-800">{call.nextStep}</p>
            </div>
            <div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={copyHandoffNote}
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <ClipboardCopy className="h-3 w-3 mr-1" />
                    Copy handoff note
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Caller information */}
        <Card className="gap-2 py-4">
          <CardHeader className="px-4">
            <CardTitle className="text-sm">Caller information</CardTitle>
          </CardHeader>
          <CardContent className="px-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-xs text-gray-500">Caller</dt>
                <dd className="text-gray-800">{call.callerName}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Phone</dt>
                <dd className="text-gray-800">{call.callerPhone}</dd>
              </div>
              {call.patientName && (
                <div>
                  <dt className="text-xs text-gray-500">Patient</dt>
                  <dd className="text-gray-800">{call.patientName}</dd>
                </div>
              )}
              {call.callerRelationship && (
                <div>
                  <dt className="text-xs text-gray-500">Relationship</dt>
                  <dd className="text-gray-800">{call.callerRelationship}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-gray-500">Practice</dt>
                <dd className="text-gray-800">{practiceName}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Scheduling request the assistant could not book — staff completes it. */}
        {call.outcome === "APPOINTMENT_REQUESTED" && (
          <Card className="gap-2 py-4 bg-blue-50 border-blue-200">
            <CardContent className="px-4 flex items-start gap-2">
              <CalendarClock className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-900">
                The assistant took a message for this scheduling request instead
                of booking it; staff completes it from the follow-up task below.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Scheduled appointment — booked by the assistant on the call. */}
        {call.outcome === "APPOINTMENT_SCHEDULED" && (
          <Card className="gap-2 py-4 bg-emerald-50 border-emerald-200">
            <CardContent className="px-4 space-y-2">
              <div className="flex items-start gap-2">
                <CalendarClock className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-900">
                  The assistant scheduled this appointment directly — no staff
                  action required.
                </p>
              </div>
              {call.appointment && (
                <dl
                  className="ml-6 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm"
                  data-testid="call-appointment"
                >
                  <div>
                    <dt className="text-xs text-emerald-700">Provider</dt>
                    <dd className="text-emerald-900">
                      {call.appointment.providerName}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-emerald-700">When</dt>
                    <dd className="text-emerald-900">
                      {formatCallTime(call.appointment.startIso, timezone)} ·{" "}
                      {formatDurationMinutes(call.appointment)} min
                    </dd>
                  </div>
                  {call.appointment.callbackNumber && (
                    <div>
                      <dt className="text-xs text-emerald-700">Callback</dt>
                      <dd className="text-emerald-900">
                        {call.appointment.callbackNumber}
                      </dd>
                    </div>
                  )}
                </dl>
              )}
              {call.appointmentType && (
                <div className="flex items-center gap-2 ml-6">
                  <Link to={APPOINTMENT_CATEGORIES_PATH}>
                    <Badge
                      variant="outline"
                      className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs hover:bg-emerald-200 cursor-pointer"
                    >
                      {call.appointmentType}
                    </Badge>
                  </Link>
                  <span className="text-[11px] text-emerald-700">
                    Detected from{" "}
                    <Link
                      to={APPOINTMENT_CATEGORIES_PATH}
                      className="underline hover:text-emerald-900"
                    >
                      appointment types
                    </Link>
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Knowledge attribution — the minimal two-tier knowledge surface */}
        <Card className="gap-2 py-4">
          <CardHeader className="px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <BookOpenCheck className="h-4 w-4 text-gray-500" />
              Knowledge bank
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4" data-testid="knowledge-attribution">
            {citedEntries.length > 0 ? (
              <div className="space-y-2">
                {citedEntries.map((entry) => (
                  <div key={entry.id} className="flex items-start gap-2">
                    <Badge
                      variant="secondary"
                      className="text-[10px] whitespace-nowrap shrink-0"
                    >
                      {entry.tier === "ORG"
                        ? "All locations"
                        : `${practiceName} only`}
                    </Badge>
                    <div className="text-sm text-gray-800">
                      <Link
                        to={knowledgeEntryPath(entry.id)}
                        data-testid={`kb-entry-link-${entry.id}`}
                        className="hover:underline text-brand"
                      >
                        “{entry.question}”
                      </Link>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-500 pt-1">
                  {call.followUpTask
                    ? "Not every question on this call had a knowledge bank answer — a follow-up task covers the rest. "
                    : ""}
                  Each cited reply is marked in the transcript below, and every
                  entry above opens in the{" "}
                  <Link
                    to={KNOWLEDGE_BANK_PATH}
                    className="text-brand hover:underline"
                  >
                    knowledge bank
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-700">
                No knowledge bank answer was available for this question
                {call.followUpTask ? " — a follow-up task was created." : "."}{" "}
                The assistant never improvises answers outside the
                knowledge bank.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Follow-up task */}
        {call.followUpTask && (
          <Card className="gap-2 py-4 bg-amber-50 border-amber-200">
            <CardHeader className="px-4">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <span>Follow-up task</span>
                <Badge
                  variant="outline"
                  className="bg-amber-100 text-amber-800 border-amber-300"
                >
                  Pending
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 space-y-3">
              <p className="text-sm text-gray-800">
                {call.followUpTask.description}
              </p>
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">
                  Information collected on the call
                </h4>
                <ul className="list-disc list-inside text-sm text-gray-800 space-y-0.5">
                  {call.followUpTask.patientInfoCollected.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-gray-600">
                Callback: {call.followUpTask.callbackNumber} · due by{" "}
                {formatCallTime(call.followUpTask.dueBy, timezone)}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Transcript */}
        <Card className="gap-0 py-2">
          <CardContent className="px-4">
            <Accordion type="single" collapsible defaultValue="transcript">
              <AccordionItem value="transcript" className="border-b-0">
                <AccordionTrigger className="text-sm">
                  Full transcript
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2.5" data-testid="call-transcript">
                    {call.transcript.map((turn, index) => {
                      const isAgent = turn.speaker === "AGENT";
                      const entry =
                        turn.knowledgeEntryId === undefined
                          ? undefined
                          : lookup(turn.knowledgeEntryId);
                      const isExpanded = expandedCitations[index] === true;
                      return (
                        <div key={index} className="flex gap-2.5">
                          <span className="text-[10px] text-gray-400 w-9 shrink-0 pt-2.5 text-right tabular-nums">
                            {formatOffset(turn.offsetSeconds)}
                          </span>
                          <div
                            className={`flex-1 rounded-lg border px-3 py-2 ${
                              isAgent
                                ? "bg-brand-50 border-brand-100"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div
                              className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${
                                isAgent ? "text-brand" : "text-gray-500"
                              }`}
                            >
                              {isAgent ? "Agent" : "Caller"}
                            </div>
                            <p className="text-sm text-gray-800">{turn.text}</p>
                            {entry && (
                              <div className="mt-2 pt-2 border-t border-brand-100">
                                <button
                                  type="button"
                                  aria-expanded={isExpanded}
                                  data-testid={`kb-citation-toggle-${index}`}
                                  className="flex items-center gap-1.5 text-[11px] font-medium text-brand hover:underline"
                                  onClick={() => toggleCitation(index)}
                                >
                                  <BookOpenCheck className="h-3.5 w-3.5 shrink-0" />
                                  {entry.tier === "ORG"
                                    ? "Organization knowledge bank"
                                    : "Practice knowledge bank"}
                                  <ChevronDown
                                    className={`h-3 w-3 transition-transform ${
                                      isExpanded ? "rotate-180" : ""
                                    }`}
                                  />
                                </button>
                                {isExpanded && (
                                  <div
                                    className="mt-2 rounded-md border border-brand-100 bg-white px-2.5 py-2 space-y-1.5"
                                    data-testid={`kb-citation-${index}`}
                                  >
                                    <p className="text-[11px] font-medium text-brand">
                                      {sourceLabel(entry)}
                                    </p>
                                    <p className="text-xs text-gray-800">
                                      <span className="text-gray-500">
                                        Cited entry:{" "}
                                      </span>
                                      “{entry.question}”
                                    </p>
                                    <p className="text-xs text-gray-700">
                                      {entry.answer}
                                    </p>
                                    <p className="text-[10px] text-gray-500">
                                      Last updated{" "}
                                      {new Date(entry.lastReviewedAt).toLocaleDateString()}
                                    </p>
                                    <Link
                                      to={knowledgeEntryPath(entry.id)}
                                      data-testid={`kb-citation-link-${index}`}
                                      className="inline-flex items-center gap-1 text-[11px] font-medium text-brand hover:underline"
                                    >
                                      Open in knowledge bank
                                      <ArrowUpRight className="h-3 w-3 shrink-0" />
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}
                            {turn.appointmentTypeCited && (
                              <div className="mt-2 pt-2 border-t border-emerald-200">
                                <button
                                  type="button"
                                  aria-expanded={isExpanded}
                                  data-testid={`appt-type-citation-toggle-${index}`}
                                  className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 hover:underline"
                                  onClick={() => toggleCitation(index)}
                                >
                                  <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                                  Appointment types
                                  <ChevronDown
                                    className={`h-3 w-3 transition-transform ${
                                      isExpanded ? "rotate-180" : ""
                                    }`}
                                  />
                                </button>
                                {isExpanded && (
                                  <div
                                    className="mt-2 rounded-md border border-emerald-200 bg-white px-2.5 py-2 space-y-1.5"
                                    data-testid={`appt-type-citation-${index}`}
                                  >
                                    <p className="text-[11px] font-medium text-emerald-700">
                                      Matched configured appointment type
                                    </p>
                                    <p className="text-xs text-gray-800">
                                      <span className="text-gray-500">
                                        Appointment type:{" "}
                                      </span>
                                      {turn.appointmentTypeCited.name}
                                    </p>
                                    <p className="text-xs text-gray-700">
                                      {turn.appointmentTypeCited.description}
                                    </p>
                                    <p className="text-[10px] text-gray-500">
                                      Duration: {turn.appointmentTypeCited.durationMinutes} minutes
                                    </p>
                                    <Link
                                      to={APPOINTMENT_CATEGORIES_PATH}
                                      data-testid={`appt-type-citation-link-${index}`}
                                      className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:underline"
                                    >
                                      Open appointment types
                                      <ArrowUpRight className="h-3 w-3 shrink-0" />
                                    </Link>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-3 border-t text-xs text-gray-500 flex items-center gap-1.5">
                    <Volume2 className="h-3.5 w-3.5 shrink-0" />
                    {call.recordingUrl ? (
                      <audio
                        controls
                        preload="none"
                        src={call.recordingUrl}
                        className="h-8 w-full max-w-md"
                        data-testid="call-recording"
                      />
                    ) : (
                      <span>No recording yet.</span>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default InboundCallDetail;
